import React, { useMemo } from "react";
import { Box } from "@chakra-ui/react";
import Joi from "joi";
import { useTranslation } from "react-i18next";
import { captureException } from "@sentry/nextjs";

import useApiEndpoint from "hooks/useApiEndpoint";
import { GlobalStateContext } from "contexts/GlobalState";
import { EApiReportReasonId } from "utils/offer";
import ReportOfferApiEndpoint from "utils/offer/api/ReportOffer";
import useForm, { OnFormSubmitType } from "hooks/useForm";
import { useAlertModal } from "../useAlertModal";
import { useCreateModal, InitialUseCreateModalType } from "../ModalsProvider";
import Select from "components/Chakra/Selectbox";
import AnimatedInput from "components/Chakra/AnimatedInput";
import Textarea from "components/Chakra/Textarea";
import Button from "components/Chakra/Button";
import { DetailData } from "@ud/api/newPhpApi/__generated__/newPhpApi.generated";

type ReportModalProps = {
  offer: DetailData;
};
interface IFormValues {
  reason: EApiReportReasonId;
  email: string;
  description: string;
}

const FormSchema = Joi.object({
  reason: Joi.string().required(),
  email: Joi.string().required(),
  description: Joi.string().min(10).max(150).required(),
});

const ReportModal: React.FC<ReportModalProps> = ({ offer }) => {
  const { user } = React.useContext(GlobalStateContext);
  const { callEndpoint: reportOfferApi } = useApiEndpoint(
    ReportOfferApiEndpoint,
  );
  const { openModal: openAlertModal } = useAlertModal();
  const { t } = useTranslation();

  const initialValues: IFormValues = {
    reason: EApiReportReasonId.SPAM,
    email: user ? user.email : "",
    description: "",
  };

  const { registerWithError, handleSubmit, watch } = useForm<IFormValues>(
    FormSchema,
    {
      defaultValues: initialValues,
    },
  );

  const REASON_OPTIONS = useMemo(
    () => [
      {
        value: EApiReportReasonId.SPAM,
        label: t("reportDialog.reasonEnum.1_spam"),
      },
      {
        value: EApiReportReasonId.INACTIVE,
        label: t("reportDialog.reasonEnum.2_inactive"),
      },
      {
        value: EApiReportReasonId.NO_RESPONSE,
        label: t("reportDialog.reasonEnum.3_no_response"),
      },
      {
        value: EApiReportReasonId.BAD_PLACEMENT,
        label: t("reportDialog.reasonEnum.4_bad_placement"),
      },
      {
        value: EApiReportReasonId.BAD_PRICE,
        label: t("reportDialog.reasonEnum.5_bad_price"),
      },
      {
        value: EApiReportReasonId.BAD_DESCRIPTION_OR_PHOTO,
        label: t("reportDialog.reasonEnum.6_bad_description_or_photo"),
      },
      {
        value: EApiReportReasonId.OTHER,
        label: t("reportDialog.reasonEnum.7_other"),
      },
    ],
    [],
  );

  const selectForm = watch("reason");

  const handleData: OnFormSubmitType<IFormValues> = ({
    description,
    email,
    reason,
  }) => {
    reportOfferApi({
      offerId: offer.id,
      reason,
      email,
      description,
    })
      .then(() => {
        openAlertModal({
          animation: "check",
          title:
            "Děkujeme za nahlášení inzerátu, co nejdříve se na to podíváme.",
          submitText: "Zavřít",
        });
      })
      .catch((err) => {
        captureException(err);
      });
  };

  return (
    <Box textAlign="center" as="form" onSubmit={handleSubmit(handleData)}>
      <Select
        {...registerWithError("reason")}
        label="Co je špatně?"
        customError="any.required"
        color={selectForm?.includes("none") ? "ud-grey-350" : "ud-grey-600"}
        _focus={{
          borderWidth: "1px",
          borderStyle: "solid",
          borderColor: "ud-grey-500",
        }}
      >
        {REASON_OPTIONS.map((item) => (
          <option key={item.value} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
      <AnimatedInput
        {...registerWithError("email")}
        type="email"
        autoComplete="email"
        required
        label="Váš email"
      />
      <Textarea
        {...registerWithError("description")}
        label="Popis"
        required
        rows={8}
      />
      <Button type="submit" mt="20px">
        Odeslat
      </Button>
    </Box>
  );
};

export const useReportModal: InitialUseCreateModalType<ReportModalProps> = () =>
  useCreateModal("reportModal", ReportModal as React.FC, null, null, {
    size: "md",
    variant: "primary",
  });
